import type { ComponentProps } from "react";
import React from "react";
import { ToggleButton as RACToggleButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { focusRing } from "../../utils";
import { controlStyles, type Size } from "../../utils/controlStyles";
import { Icon } from "../Icon";

type IconName = ComponentProps<typeof Icon>["name"];

type ToggleButtonVariant = "primary";
type LegacyToggleButtonVariant = "default" | "brand" | "secondary";

function normalizeToggleButtonVariant(
  _variant: ToggleButtonVariant | LegacyToggleButtonVariant | undefined
): ToggleButtonVariant {
  return "primary";
}

export interface ToggleButtonProps {
  variant?: ToggleButtonVariant | LegacyToggleButtonVariant;

  size?: Size;
  className?: string;
  children: React.ReactNode;
  onChange?: (isSelected: boolean) => void;
  onPress?: () => void;
  isSelected?: boolean;
  isDisabled?: boolean;
  borderRadius?: "left" | "right";
  borderRight?: boolean;
  borderLeft?: boolean;
  icon?: IconName;
  iconWeight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
  iconPosition?: "left" | "right";
  /** Value used for group selection state */
  value?: string;
  isLoading?: boolean;
  loadingText?: string;
  loadingIndicator?: React.ReactNode;
  badgeNumber?: number;
  badgeVariant?: "primary" | "destructive";
  badgePosition?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
}

const baseStyles =
  "flex font-medium justify-center items-center gap-2 text-center transition whitespace-nowrap box-border";

const variantStyles = {
  primary: {
    selected:
      "bg-action-primary text-[color:var(--color-action-primary-text)] hover:bg-action-primary-hover forced-colors:bg-[ButtonText] forced-colors:text-[ButtonFace] border-2 border-transparent",
    unselected:
      "bg-transparent text-action-primary border-2 border-border-muted hover:bg-text-primary/[0.05] forced-colors:border-[ButtonBorder] forced-colors:bg-[ButtonFace] forced-colors:text-[ButtonText]",
  },
} as const;

const disabledStyles = "opacity-[--control-hover-opacity] cursor-not-allowed pointer-events-none grayscale";
const enabledStyles = "opacity-100 cursor-pointer";

const badgePositionStyles = {
  "top-right": "-right-2 -top-2",
  "top-left": "-left-2 -top-2",
  "bottom-right": "-right-2 -bottom-2",
  "bottom-left": "-left-2 -bottom-2",
} as const;

const badgeVariantStyles = {
  primary: "bg-action-primary text-[color:var(--color-action-primary-text)]",
  destructive: "bg-feedback-error-border text-text-inverse",
} as const;

function getToggleButtonStyles(props: ToggleButtonProps) {
  const { variant = "primary", size = "md", isDisabled, isSelected, borderRadius, borderRight, borderLeft } = props;
  const normalizedVariant = normalizeToggleButtonVariant(variant);

  let styles = `${baseStyles} ${isSelected ? variantStyles[normalizedVariant].selected : variantStyles[normalizedVariant].unselected}`;

  // Add size styles using control-specific variables
  styles += ` ${controlStyles[size].text} ${controlStyles[size].height} ${controlStyles[size].padding} ${controlStyles[size].gap}`;

  // Add state styles
  styles += ` ${isDisabled ? disabledStyles : enabledStyles}`;

  // Add border styles
  if (borderRadius === "left") styles += " rounded-l-sm";
  if (borderRadius === "right") styles += " rounded-r-sm";
  if (borderRight) styles += " border-r";
  if (borderLeft) styles += " border-l";

  // Add focus ring
  styles += ` ${focusRing()}`;
  if (normalizedVariant === "primary") {
    styles += " focus-visible:outline-action-primary";
  }

  return twMerge(styles);
}

export function ToggleButton(props: ToggleButtonProps) {
  const {
    className,
    children,
    onPress,
    icon,
    iconPosition = "left",
    iconWeight,
    isLoading = false,
    loadingText,
    loadingIndicator,
    size = "md",
    badgeNumber,
    badgeVariant = "primary",
    badgePosition = "top-right",
    ...rest
  } = props;

  const iconElement = icon ? <Icon name={icon} size={size} weight={iconWeight} /> : null;

  const renderChildren = () => {
    const content = (
      <>
        {iconPosition === "left" && iconElement}
        {children}
        {iconPosition === "right" && iconElement}
      </>
    );

    if (isLoading) {
      return (
        <div className="relative inline-flex items-center justify-center">
          <div className="absolute inset-0 flex items-center justify-center">
            {loadingIndicator || <Icon name="CircleNotch" size={size} className="animate-spin" aria-hidden="true" />}
          </div>
          <div className="invisible" aria-hidden="true">
            {content}
          </div>
          {loadingText && (
            <span className="sr-only" role="status" aria-live="polite">
              {loadingText}
            </span>
          )}
        </div>
      );
    }

    return content;
  };

  const toggleButton = (
    <RACToggleButton
      onPress={onPress}
      {...rest}
      className={twMerge(getToggleButtonStyles(props), className)}
      aria-busy={isLoading}
    >
      {renderChildren()}
    </RACToggleButton>
  );

  if (badgeNumber && badgeNumber > 0) {
    return (
      <div className="relative">
        {toggleButton}
        <span
          className={`absolute ${badgePositionStyles[badgePosition]} flex h-5 w-5 items-center justify-center rounded-full ${badgeVariantStyles[badgeVariant]} text-xs`}
          aria-label={`${badgeNumber} items`}
        >
          {badgeNumber}
        </span>
      </div>
    );
  }

  return toggleButton;
}
